import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  init,
  webHidPairing,
  RequestedBrowserTransport,
  LogLevel,
  EasyCallControlFactory,
  IApi,
  IDevice,
  IMultiCallControl,
  IConfig,
  HoldState,
  MuteState,
} from '@gnaudio/jabra-js/browser-esm/index.js';
import { BehaviorSubject, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { filter, distinctUntilChanged, take, withLatestFrom, debounceTime } from 'rxjs/operators';
import { SipService, ActiveCallInfo } from '../sip/sip.service';

/**
 * JabraService handles interactions with Jabra headset devices.
 * It follows an event-driven architecture where:
 * 1. Headset events are captured and converted to Observable streams
 * 2. State changes are emitted as events for components to handle
 * 3. No direct SIP or UI operations are performed
 */
@Injectable({
  providedIn: 'root',
})
export class JabraService implements OnDestroy {  
  private jabraSdk: IApi | null = null;
  private activeDevice: IDevice | null = null;
  private easyCallControl: IMultiCallControl | null = null;
  private eccFactory: EasyCallControlFactory | null = null;

  // Track previously disconnected devices
  private previouslyDisconnectedDevices = new Set<string>();

  // Add a subject to emit reconnection events
  private _onDeviceReconnected = new Subject<void>();
  public onDeviceReconnected$: Observable<void> = this._onDeviceReconnected.asObservable();

  // Keep track of SDK initialization status
  private _isSdkInitialized = new BehaviorSubject<boolean>(false);
  public isSdkInitialized$: Observable<boolean> = this._isSdkInitialized.asObservable();

  // Keep track of device connection status
  private _isConnected = new BehaviorSubject<boolean>(false);
  public isConnected$: Observable<boolean> = this._isConnected.asObservable();

  // Track available devices
  private _deviceList = new BehaviorSubject<IDevice[]>([]);
  public deviceList$: Observable<IDevice[]> = this._deviceList.asObservable();

  // Name of the connected device
  private _deviceName = new BehaviorSubject<string | null>(null);
  public deviceName$: Observable<string | null> = this._deviceName.asObservable();

  // State tracking from the headset
  private _isMuted = new BehaviorSubject<boolean>(false);
  public isMuted$: Observable<boolean> = this._isMuted.asObservable().pipe(distinctUntilChanged());

  private _isOnHold = new BehaviorSubject<boolean>(false);
  public isOnHold$: Observable<boolean> = this._isOnHold.asObservable().pipe(distinctUntilChanged());

  private _ongoingCalls = new BehaviorSubject<number>(0);
  public ongoingCalls$: Observable<number> = this._ongoingCalls.asObservable().pipe(distinctUntilChanged());

  // Synchronization flags to prevent circular updates
  private processingMuteEvent = false;
  private processingHoldEvent = false;
  private processingCallStateEvent = false;
  private processingCallEndEvent = false;

  // --- Events emitted FROM the headset TO the application ---
  private _onAcceptCall = new Subject<void>();
  public onAcceptCall$: Observable<void> = this._onAcceptCall.asObservable();

  private _onRejectCall = new Subject<void>();
  public onRejectCall$: Observable<void> = this._onRejectCall.asObservable();

  private _onEndCall = new Subject<void>();
  public onEndCall$: Observable<void> = this._onEndCall.asObservable();

  private _onToggleHold = new Subject<boolean>(); // Emits true for hold, false for resume
  public onToggleHold$: Observable<boolean> = this._onToggleHold.asObservable();

  private _onToggleMute = new Subject<boolean>(); // Emits true for mute, false for unmute
  public onToggleMute$: Observable<boolean> = this._onToggleMute.asObservable();

  private _onSwapCalls = new Subject<ActiveCallInfo[]>();
  public onSwapCalls$: Observable<ActiveCallInfo[]> = this._onSwapCalls.asObservable();

  // New events for handling ringing calls during reset
  private _onCancelOutgoingRingingCall = new Subject<void>();
  public onCancelOutgoingRingingCall$: Observable<void> = this._onCancelOutgoingRingingCall.asObservable();

  private _onRejectIncomingRingingCall = new Subject<void>();
  public onRejectIncomingRingingCall$: Observable<void> = this._onRejectIncomingRingingCall.asObservable();

  private deviceSubscriptions: Subscription[] = [];
  private callControlSubscriptions: Subscription[] = [];
  private sipSubscriptions: Subscription[] = [];
  private autoConnectSubscription: Subscription | null = null;
  private sdkInitializationPromise: Promise<void> | null = null;

  constructor(
    private ngZone: NgZone,
    private sipService: SipService
  ) {}

  // Public getters for immediate state access
  public getIsConnected(): boolean {
    return this._isConnected.value;
  }

  public getIsMuted(): boolean {
    return this._isMuted.value;
  }

  public getIsOnHold(): boolean {
    return this._isOnHold.value;
  }

  public getActiveDevice(): IDevice | null {
    return this.activeDevice;
  }

  public getDeviceList(): IDevice[] {
    return this._deviceList.value;
  }

  /**
   * Get the current ongoing calls count from the headset
   * @returns The number of ongoing calls as tracked by the headset
   */
  public getOngoingCalls(): number {
    return this._ongoingCalls.value;
  }

  /**
   * Initializes the Jabra SDK using WebHID transport. Ensures it only runs once.
   */
  async initialize(): Promise<void> {
    // Prevent multiple concurrent initializations
    if (!this.sdkInitializationPromise) {
      this.sdkInitializationPromise = this.internalInitialize();
    }
    await this.sdkInitializationPromise;
  }

  private async internalInitialize(): Promise<void> {
    if (this.jabraSdk) {
      console.log('[Jabra Service] SDK already initialized.');
      return;
    }

    console.log('[Jabra Service] Initializing SDK...');
    try {
      const config: IConfig = {
        partnerKey: 'a436-f318e2c6-513c-4616-a0a4-c1156fd267b9',
        appId: 'ng-jabra-service',
        appName: 'Angular Jabra Service',
        transport: RequestedBrowserTransport.WEB_HID,
        logger: {
          write: (logEvent: any) => {
            this.ngZone.runOutsideAngular(() => {
              const level = logEvent.level || LogLevel.INFO;
              const message = `[Jabra SDK][${logEvent.layer || 'SDK'}] ${logEvent.message}`;
              switch (level) {
                case LogLevel.ERROR: console.error(message, logEvent.error || ''); break;
                case LogLevel.WARNING: console.warn(message); break;
                case LogLevel.INFO: console.info(message); break;
                default: console.log(message); break;
              }
            });
          },
        },
      };

      this.jabraSdk = await init(config);
      this.eccFactory = new EasyCallControlFactory(this.jabraSdk);
      this._isSdkInitialized.next(true);
      console.log('[Jabra Service] SDK initialized successfully.');

      this.setupDeviceListeners();
      
      // Set up auto-connect subscription for reactive connection management
      this.setupAutoConnectSubscription();

      // Setup SIP service integration
      this.setupSipIntegration();

      // Check for initially connected devices
      if (this.jabraSdk) {
        const initialDevices = (await firstValueFrom(this.jabraSdk.deviceList)) || [];
        this._deviceList.next(initialDevices);
        
        if (initialDevices && initialDevices.length > 0) {
          console.log(`[Jabra Service] Found ${initialDevices.length} initially connected device(s).`);
          // Automatically connect to the first detected device
          this.ngZone.run(() => {
            this.handleDeviceAdded(initialDevices[0]);
          });
        } else {
          console.log('[Jabra Service] No devices detected initially.');
        }
      }
    } catch (error) {
      console.error('[Jabra Service] Failed to initialize SDK:', error);
      this._isSdkInitialized.next(false);
      this._isConnected.next(false);
      this._deviceName.next(null);
      this.sdkInitializationPromise = null; // Reset promise on failure
    }
  }

  /**
   * Sets up a subscription that automatically connects to the first available device
   * when no device is currently connected.
   */
  private setupAutoConnectSubscription(): void {
    // Clean up any existing subscription
    if (this.autoConnectSubscription) {
      this.autoConnectSubscription.unsubscribe();
      this.autoConnectSubscription = null;
    }

    // Create a new subscription that monitors device list and connection state
    this.autoConnectSubscription = this._deviceList.pipe(
      // Skip empty device lists
      filter(devices => devices.length > 0),
      // Add small debounce to avoid rapid connection attempts
      debounceTime(100),
      // Combine with current connection state
      withLatestFrom(this._isConnected),
      // Only proceed if not connected and SDK is initialized
      filter(([_, isConnected]) => !isConnected && this._isSdkInitialized.value)
    ).subscribe(([devices, _]) => {
      this.ngZone.run(() => {
        // If we have no active device but devices available, connect to first one
        if (!this.activeDevice && devices.length > 0) {
          console.log('[Jabra Service] Auto-connecting to first available device:', devices[0].name);
          this.handleDeviceAdded(devices[0]);
        }
      });
    });

    // Add to subscriptions for cleanup
    this.deviceSubscriptions.push(this.autoConnectSubscription);
  }

  /**
   * Explicitly request the user to connect a Jabra device via WebHID selection dialog
   */
  async pairDevice(): Promise<void> {
    if (!this.jabraSdk) {
      console.warn('[Jabra Service] Cannot pair device: SDK not initialized');
      await this.initialize();
    }

    // Check if we've previously disconnected devices in this session
    const hasPreviouslyDisconnectedDevices = this.previouslyDisconnectedDevices.size > 0;

    if (this.jabraSdk) {
      try {
        console.log('[Jabra Service] Opening WebHID device selection dialog...');
        await webHidPairing();
        console.log('[Jabra Service] WebHID pairing completed.');
        
        if (hasPreviouslyDisconnectedDevices) {
          // If we've previously disconnected devices, signal that a reload is needed
          console.log('[Jabra Service] Attempting to pair after previously unpaired - reload required');
          this._onDeviceReconnected.next();
        }
      } catch (error) {
        console.error('[Jabra Service] WebHID pairing failed:', error);
      }
    }
  }

  private setupDeviceListeners(): void {
    if (!this.jabraSdk) return;

    this.deviceSubscriptions.forEach(sub => sub.unsubscribe());
    this.deviceSubscriptions = [];

    // Device added event
    this.deviceSubscriptions.push(
      this.jabraSdk.deviceAdded.subscribe((device: IDevice) => {
        this.ngZone.run(() => {
          console.log(`[Jabra Service] Device attached: ${device.name}`);
          
          // Check if this device is already in our list
          const currentDevices = this._deviceList.value;
          const isDuplicate = device.serialNumber 
            ? currentDevices.some(d => d.serialNumber === device.serialNumber)
            : currentDevices.some(d => d.name === device.name);
          
          if (isDuplicate) {
            console.log(`[Jabra Service] Ignoring duplicate device: ${device.name}`);
            return;
          }
          
          // Update device list
          const devices = [...currentDevices, device];
          this._deviceList.next(devices);
          
          // If no active device, connect to this one
          if (!this.activeDevice) {
            this.handleDeviceAdded(device);
          } else {
            console.log(`[Jabra Service] Another device (${this.activeDevice.name}) is already active. Ignoring ${device.name}.`);
          }
        });
      })
    );

    // Device removed event
    this.deviceSubscriptions.push(
      this.jabraSdk.deviceRemoved.subscribe((device: IDevice) => {
        this.ngZone.run(() => {
          console.log(`[Jabra Service] Device detached: ${device.name}`);
          
          // Update device list
          const devices = this._deviceList.value.filter(d => d.serialNumber !== device.serialNumber);
          this._deviceList.next(devices);
          
          // If this was the active device, handle removal
          if (this.activeDevice && this.activeDevice.serialNumber === device.serialNumber) {
            this.handleDeviceRemoved();
            
            // If other devices are available, connect to first one
            if (devices.length > 0) {
              this.handleDeviceAdded(devices[0]);
            }
          }
        });
      })
    );

    // Device list event
    this.deviceSubscriptions.push(
      this.jabraSdk.deviceList.subscribe((devices: IDevice[]) => {
        this.ngZone.run(() => {
          console.log(`[Jabra Service] Device list updated, ${devices.length} devices available.`);
          
          // Remove duplicates based on serial number
          const uniqueDevices: IDevice[] = [];
          const seenSerialNumbers = new Set<string>();
          
          // First handle devices with valid serial numbers
          for (const device of devices) {
            // Only consider devices with a valid serial number
            if (device.serialNumber) {
              // If we haven't seen this serial number before, add it to our list
              if (!seenSerialNumbers.has(device.serialNumber)) {
                seenSerialNumbers.add(device.serialNumber);
                uniqueDevices.push(device);
              } else {
                console.log(`[Jabra Service] Ignoring duplicate device with serial number: ${device.serialNumber}`);
              }
            }
          }
          
          // Then handle devices without a serial number (less reliable)
          // Use device name as a fallback for deduplication
          const seenNames = new Set<string>();
          for (const device of devices) {
            // Handle devices without a valid serial number
            if (!device.serialNumber) {
              if (!seenNames.has(device.name)) {
                seenNames.add(device.name);
                uniqueDevices.push(device);
              } else {
                console.log(`[Jabra Service] Ignoring duplicate device with name: ${device.name} (no serial number)`);
              }
            }
          }
          
          this._deviceList.next(uniqueDevices);
        });
      })
    );
  }

  private setupSipIntegration(): void {
    // Clean up any existing subscriptions
    this.sipSubscriptions.forEach(sub => sub.unsubscribe());
    this.sipSubscriptions = [];

    // Monitor active calls and synchronize states
    this.sipSubscriptions.push(
      this.sipService.getActiveCalls().subscribe(calls => {
        // Only sync state from SIP to headset when not handling a headset-initiated event
        if (!this.processingHoldEvent && !this.processingMuteEvent && !this.processingCallStateEvent) {
          this.synchronizeHeadsetState(calls);
        }
      })
    );

    // Incoming call canceled events
    this.sipSubscriptions.push(
      this.sipService.incomingCallCanceled$.subscribe(event => {
        if (event) {
          this.ngZone.run(async () => {
            // Only signal rejection to headset for regular incoming calls, not call waiting calls
            if (event.isCallWaiting) {
              console.log(`[Jabra Service] Call waiting call ${event.callId} canceled - not signaling headset rejection`);
            } else {
              console.log('[Jabra Service] Regular incoming call canceled by remote side, signaling headset to stop ringing');
              try {
                // Signal rejection to stop the headset from ringing
                await this.signalCallRejected();
              } catch (err) {
                console.warn('Error signaling call rejection to headset:', err);
              }
            }
          });
        }
      })
    );

    // When SIP service registers/unregisters
    this.sipSubscriptions.push(
      this.sipService.getRegistrationStatus().subscribe(isRegistered => {
        console.log(`[Jabra Service] SIP registration state changed: ${isRegistered ? 'Registered' : 'Unregistered'}`);
      })
    );
  }

  /**
   * Synchronize headset state with SIP call state.
   * This is a one-way sync from SIP → Headset
   */
  private synchronizeHeadsetState(calls: ActiveCallInfo[]): void {
    if (!this.easyCallControl || !this.getIsConnected()) return;

    // Include calls in any non-idle state
  const activeCalls = calls.filter(call => 
    call.state === 'ringing' ||
    call.state === 'connected' || 
    call.state === 'held' || 
    call.state === 'transferring' ||
    call.state === 'conference'
  );
    
    // Update ongoing calls count
    const ongoingCallsCount = activeCalls.length;
    if (this._ongoingCalls.value !== ongoingCallsCount) {
      this._ongoingCalls.next(ongoingCallsCount);
      console.log(`[Jabra Service] Ongoing calls updated: ${ongoingCallsCount}`);
    }
    
    // If we have no calls, reset headset state
    if (activeCalls.length === 0) {
      this.resetHeadsetState().catch(err => 
        console.warn('[Jabra Service] Error resetting headset state:', err)
      );
      return;
    }
    
    // Get active call for synchronization (prefer connected calls)
    const activeCall = calls.find(call => 
      (call.state === 'connected' || call.state === 'conference') && !call.isIncoming
    );

    if (activeCall) {
      // Synchronize mute state
      if (!this.processingMuteEvent && this._isMuted.value !== activeCall.isMuted) {
        this.processingCallStateEvent = true;
        this.signalMute(activeCall.isMuted)
          .catch(err => console.warn('[Jabra Service] Error syncing mute state:', err))
          .finally(() => {
            this.processingCallStateEvent = false;
          });
      }
      
      // Synchronize hold state
      const isOnHold = activeCall.state === 'held';
      if (!this.processingHoldEvent && this._isOnHold.value !== isOnHold) {
        this.processingCallStateEvent = true;
        this.signalHold(isOnHold)
          .catch(err => console.warn('[Jabra Service] Error syncing hold state:', err))
          .finally(() => {
            this.processingCallStateEvent = false;
          });
      }
    }
  }

  /**
   * Reset the headset to its default state
   * This can be used when the headset is in an inconsistent state
   */
  async resetHeadset(): Promise<void> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn('[Jabra Service] Cannot reset headset: Headset not connected or call control not available.');
      return;
    }
    
    console.log('[Jabra Service] Performing headset reset...');
    
    try {
      // First, try to end any ongoing call that the headset knows about
      try {
        if (this._ongoingCalls.value > 0) {
          await this.easyCallControl.endCall();
          this._ongoingCalls.next(0);
        }
      } catch (error) {
        // Ignore errors related to ending calls
        if (error instanceof Error && !error.message.includes('no calls in progress')) {
          console.warn('[Jabra Service] Error ending calls during reset:', error);
        }
      }
      
      // Emit events to cancel any outgoing ringing calls that the headset might not know about
      console.log('[Jabra Service] Emitting event to cancel outgoing ringing calls');
      this._onCancelOutgoingRingingCall.next();
      
      // Emit events to reject any incoming ringing calls that the headset might not know about
      console.log('[Jabra Service] Emitting event to reject incoming ringing calls');
      this._onRejectIncomingRingingCall.next();
      
      // Reset mute state if needed
      try {
        if (this._isMuted.value) {
          await this.easyCallControl.unmute();
          this._isMuted.next(false);
        }
      } catch (error) {
        console.warn('[Jabra Service] Error resetting mute state:', error);
      }
      
      // Reset hold state
      this._isOnHold.next(false);
      
      console.log('[Jabra Service] Headset reset completed.');
    } catch (error) {
      console.error('[Jabra Service] Error resetting headset:', error);
      throw error;
    }
  }

  /**
   * Reset headset to idle state
   * This is used internally when calls end
   */
  private async resetHeadsetState(): Promise<void> {
    if (!this.easyCallControl) return;
    
    try {
      // Unmute if muted
      if (this._isMuted.value) {
        await this.easyCallControl.unmute();
        this._isMuted.next(false);
      }
      
      // End any ongoing call state
      await this.signalCallEnded(true);
      
      // Ensure hold state is cleared
      this._isOnHold.next(false);
    } catch (error) {
      console.error('[Jabra Service] Error resetting headset state:', error);
    }
  }

  /**
   * Connect to a specific Jabra device
   */
  async connectDevice(device: IDevice): Promise<void> {
    if (this.activeDevice) {
      // If this is already the active device, do nothing
      if (this.activeDevice.serialNumber === device.serialNumber) {
        console.log(`[Jabra Service] Device ${device.name} is already active.`);
        return;
      }
      
      // Disconnect the current device first
      this.handleDeviceRemoved();
    }
    
    // Connect to the new device
    await this.handleDeviceAdded(device);
  }

  /**
   * Disconnect a Jabra device and revoke HID access permission
   * This completely disconnects the device and removes browser permissions
   */
  async disconnectDevice(): Promise<void> {
    if (!this._isConnected.value || !this.activeDevice || !this.jabraSdk) {
      console.warn('[Jabra Service] No device to disconnect');
      return;
    }
    
    console.log(`[Jabra Service] Disconnecting device ${this.activeDevice.name} and revoking HID access...`);
    
    try {      
      // Get device name and serial number before disconnecting
      const deviceName = this.activeDevice.name;
      const deviceSerialNumber = this.activeDevice.serialNumber;
      
      // Disconnect the device
      this.handleDeviceRemoved();
      
      // Now try to revoke the HID access permission using WebHID API
      try {
        // Check if the WebHID API is available
        if (typeof window !== 'undefined' && 'navigator' in window) {
          const nav = window.navigator as any;
          if (nav && 'hid' in nav && nav.hid) {
            const hid = nav.hid;
            // Check if getDevices exists and is a function
            if ('getDevices' in hid && typeof hid.getDevices === 'function') {
              const devices = await hid.getDevices();
              if (Array.isArray(devices)) {
                // Filter to find Jabra devices
                const jabraDevices = devices.filter(device => {
                  if (!device) return false;
                  const productName = device.productName || '';
                  const manufacturerName = device.manufacturerName || '';
                  return productName.includes('Jabra') || 
                        manufacturerName.includes('GN') || 
                        manufacturerName.includes('Jabra');
                });
                console.log(`[Jabra Service] Found ${jabraDevices.length} Jabra HID devices`);
                
                // Forget each device
                for (const device of jabraDevices) {
                  try {
                    // Close the device connection if it's open
                    if ('opened' in device && device.opened && 'close' in device && typeof device.close === 'function') {
                      await device.close();
                    }
                    // Attempt to forget the device permissions
                    if ('forget' in device && typeof device.forget === 'function') {
                      await device.forget();
                      console.log(`[Jabra Service] Successfully revoked permissions for ${device.productName || 'unknown device'}`);
                      
                      // Remove this device from the device list
                      if (deviceSerialNumber) {
                        // Update device list to remove the disconnected device
                        const updatedDeviceList = this._deviceList.value.filter(d => d.serialNumber !== deviceSerialNumber);
                        this._deviceList.next(updatedDeviceList);
                      }
                    }
                  } catch (err) {
                    console.warn(`[Jabra Service] Could not forget device ${device.productName || 'unknown'}:`, err);
                  }
                }
              }
            }
          } else {
            console.warn('[Jabra Service] WebHID API not available - cannot revoke permissions');
          }
        }
      } catch (err) {
        console.error('[Jabra Service] Error revoking HID access:', err);
      }
  
      console.log(`[Jabra Service] Device ${deviceName} disconnected`);
    } catch (error) {
      console.error('[Jabra Service] Error disconnecting device:', error);
      throw error;
    }
  }

  private async handleDeviceAdded(device: IDevice): Promise<void> {
    if (!this.eccFactory) {
      console.error('[Jabra Service] EasyCallControlFactory not initialized.');
      return;
    }
    // This method should be running in the Angular zone

    console.log(`[Jabra Service] Connecting to device: ${device.name}`);
    
    // Check if this is a reconnected device
    const serialNumber = device.serialNumber || '';
    if (serialNumber && this.previouslyDisconnectedDevices.has(serialNumber)) {
      console.log(`[Jabra Service] Device ${device.name} (${serialNumber}) has been reconnected.`);
      this._onDeviceReconnected.next();
    }
    
    this.activeDevice = device;
    this._deviceName.next(device.name);

    try {
      if (this.eccFactory.supportsEasyCallControl(device)) {
        this.easyCallControl = await this.eccFactory.createMultiCallControl(device);
        console.log(`[Jabra Service] Easy Call Control enabled for ${device.name}.`);
        this._isConnected.next(true);
        this.setupCallControlListeners();
        this.reflectInitialHeadsetState();
      } else {
        console.warn(`[Jabra Service] Device ${device.name} does not support Easy Call Control.`);
        this._isConnected.next(false);
      }
    } catch (error) {
      console.error(`[Jabra Service] Failed to enable Easy Call Control for ${device.name}:`, error);
      this._isConnected.next(false);
      this._deviceName.next(null);
      this.activeDevice = null;
    }
  }

  private handleDeviceRemoved(): void {
    // This method should be running in the Angular zone
    if (this.activeDevice) {
      console.log(`[Jabra Service] Disconnecting device ${this.activeDevice.name}`);
      
      // Store device serial number before clearing it
      if (this.activeDevice.serialNumber) {
        this.previouslyDisconnectedDevices.add(this.activeDevice.serialNumber);
      }
    }
    
    this.cleanupCallControlListeners();
    this.easyCallControl = null;
    this.activeDevice = null;
    this._isConnected.next(false);
    this._deviceName.next(null);
    this._isMuted.next(false);
    this._isOnHold.next(false);
    this._ongoingCalls.next(0);
  }

  private reflectInitialHeadsetState(): void {
    // This method should be running in the Angular zone
    if (!this.easyCallControl) return;

    console.log("[Jabra Service] Reflecting initial headset state...");
    const muteStatePromise = firstValueFrom(
      this.easyCallControl.muteState.pipe(filter(state => state !== undefined))
    );
    
    const holdStatePromise = firstValueFrom(
      this.easyCallControl.holdState.pipe(filter(state => state !== undefined))
    );

    Promise.all([muteStatePromise, holdStatePromise])
      .then(([initialMuteState, initialHoldState]) => {
        // We are already in the Angular zone from the caller
        
        const isMuted = initialMuteState === MuteState.MUTED;
        this._isMuted.next(isMuted);
        console.log(`[Jabra Service] Initial mute state: ${isMuted ? 'Muted' : 'Unmuted'}`);

        const isOnHold = initialHoldState === HoldState.ON_HOLD;
        this._isOnHold.next(isOnHold);
        console.log(`[Jabra Service] Initial hold state: ${isOnHold ? 'Held' : 'Not Held'}`);
      })
      .catch(error => {
        console.error("[Jabra Service] Error reflecting initial headset state:", error);
      });
  }

  private setupCallControlListeners(): void {
    if (!this.easyCallControl) return;

    this.cleanupCallControlListeners(); // Ensure no duplicate listeners
    console.log('[Jabra Service] Setting up call control listeners...');

    // Mute State Changes from Headset
    this.callControlSubscriptions.push(
      this.easyCallControl.muteState
        .pipe(
          filter(state => state !== undefined),
          // Ensures we only process actual changes
          distinctUntilChanged()
        )
        .subscribe((muteState: MuteState) => {
          this.ngZone.run(() => {
            // Ignore if we're already processing a mute event or call state event
            if (this.processingMuteEvent || this.processingCallStateEvent) {
              console.log(`[Jabra Service] Ignoring headset mute state change while processing another operation`);
              return;
            }

            const isMuted = muteState === MuteState.MUTED;
            // Only emit event if state truly changed
            if (this._isMuted.value !== isMuted) {
              console.log(`[Jabra Service] Headset mute button pressed: ${isMuted ? 'Muted' : 'Unmuted'}`);
              
              // Set processing flag to prevent loops
              this.processingMuteEvent = true;
              
              // Update internal state
              this._isMuted.next(isMuted);
              
              // Notify component about mute change
              this._onToggleMute.next(isMuted);
              
              // Clear processing flag after a small delay to allow state to settle
              setTimeout(() => {
                this.processingMuteEvent = false;
              }, 500);
            }
          });
        })
    );

    // Hold State Changes from Headset
    this.callControlSubscriptions.push(
      this.easyCallControl.holdState
        .pipe(filter(state => state !== undefined), distinctUntilChanged())
        .subscribe((holdState: HoldState) => {
          this.ngZone.run(() => {
            // Ignore if we're already processing a hold event or call state event
            if (this.processingHoldEvent || this.processingCallStateEvent) {
              console.log(`[Jabra Service] Ignoring headset hold state change while processing another operation`);
              return;
            }
            
            const isOnHold = holdState === HoldState.ON_HOLD;
            // Only emit event if state truly changed
            if (this._isOnHold.value !== isOnHold) {
              console.log(`[Jabra Service] Headset hold button pressed: ${isOnHold ? 'Held' : 'Resumed'}`);
              
              // Set processing flag
              this.processingHoldEvent = true;
              
              // Update internal state
              this._isOnHold.next(isOnHold);
              
              // Notify component about hold change
              this._onToggleHold.next(isOnHold);
              
              // Clear processing flag after a delay
              setTimeout(() => {
                this.processingHoldEvent = false;
              }, 500);
            }
          });
        })
    );

    // Ongoing Calls Count
    this.callControlSubscriptions.push(
      this.easyCallControl.ongoingCalls
        .pipe(filter(count => count !== undefined), distinctUntilChanged())
        .subscribe((count: number) => {
          this.ngZone.run(() => {
            // Get previous count
            const previousCount = this._ongoingCalls.value;
            console.log(`[Jabra Service] Headset ongoing calls count changed: ${previousCount} -> ${count}`);
            
            // Update our stored count
            this._ongoingCalls.next(count);
            
            // Only emit end call event if not processing a call end event
            // and if calls went from > 0 to 0 (indicating headset-initiated end)
            if (previousCount > 0 && count === 0 && !this.processingCallEndEvent) {
              console.log('[Jabra Service] Call ended from headset detected');
              this._onEndCall.next();
            }
          });
        })
    );

    // Call Swap Request
    this.callControlSubscriptions.push(
      this.easyCallControl.swapRequest.subscribe(() => {
        this.ngZone.run(() => {
          console.log(`[Jabra Service] Headset swap button pressed`);
          
          // Get active calls to pass to the component
          this.sipService.getActiveCalls()
            .pipe(take(1))
            .subscribe(activeCalls => {
              // Let the component handle the call swapping logic
              this._onSwapCalls.next(activeCalls);
            });
        });
      })
    );

    console.log('[Jabra Service] Call control listeners set up successfully.');
  }

  private cleanupCallControlListeners(): void {
    console.log('[Jabra Service] Cleaning up call control listeners.');
    this.callControlSubscriptions.forEach(sub => sub.unsubscribe());
    this.callControlSubscriptions = [];
  }

  // --- Methods to signal state changes TO the headset ---

  /**
   * Signal an incoming call to the headset
   * @returns Promise resolving to true if call was accepted, false if rejected
   */
  async signalIncomingCall(): Promise<boolean> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn('[Jabra Service] Cannot signal incoming call: Headset not connected or call control not available.');
      return false;
    }
    
    console.log('[Jabra Service] Signaling incoming call to headset...');
    try {
      // This promise resolves based on headset interaction
      const accepted = await this.easyCallControl.signalIncomingCall();
      console.log(`[Jabra Service] Incoming call ${accepted ? 'accepted' : 'rejected'} by headset`);
      
      if (accepted) {
        this._onAcceptCall.next();
      } else {
        this._onRejectCall.next();
      }
      return accepted;
    } catch (error) {
      console.error('[Jabra Service] Error signaling incoming call:', error);
      // Try to ensure headset is in idle state on error
      await this.signalCallEnded().catch(() => {});
      return false;
    }
  }

  /**
   * Signal call start/acceptance to the headset
   */
  async signalCallAccepted(): Promise<void> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn('[Jabra Service] Cannot signal call accepted: Headset not connected or call control not available.');
      return;
    }
    
    console.log('[Jabra Service] Signaling call accepted to headset...');
    try {
      await this.easyCallControl.acceptIncomingCall();
      // Update internal state tracking AFTER successful signal
      this._isOnHold.next(false);
    } catch (error) {
      console.error('[Jabra Service] Error signaling call accepted:', error);
    }
  }

  /**
   * Signal outgoing call start to the headset
   */
  async signalOutgoingCall(): Promise<void> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn('[Jabra Service] Cannot signal outgoing call: Headset not connected or call control not available.');
      return;
    }
    
    console.log('[Jabra Service] Signaling outgoing call to headset...');
    try {
      await this.easyCallControl.startCall();
    } catch (error) {
      console.error('[Jabra Service] Error signaling outgoing call:', error);
    }
  }

  /**
   * Signal call end to the headset
   * @param forceSignal Optional parameter to force signaling even if ongoingCalls is 0
   */
  async signalCallEnded(forceSignal: boolean = false): Promise<void> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn('[Jabra Service] Cannot signal call ended: Headset not connected or call control not available.');
      return;
    }
    
    // Set processing flag to prevent circular call ending
    this.processingCallEndEvent = true;
    
    try {
      // Get the current SIP call state to check if we have remaining calls
      const activeCalls = await firstValueFrom(this.sipService.getActiveCalls());
      const hasRemainingCalls = activeCalls.some(call => 
        call.state === 'connected' || 
        call.state === 'held' || 
        call.state === 'conference'
      );
      
      // Only actually end the call on the headset if this is truly the last call
      if ((forceSignal || this._ongoingCalls.value > 0) && !hasRemainingCalls) {
        console.log(`[Jabra Service] Signaling call ended to headset (forceSignal: ${forceSignal}, ongoingCalls: ${this._ongoingCalls.value}, hasRemainingCalls: false)...`);
        
        await this.easyCallControl.endCall();
        // Update internal state tracking AFTER successful signal
        this._isOnHold.next(false);
        this._ongoingCalls.next(0);
      } else if (hasRemainingCalls) {
        console.log('[Jabra Service] Not ending call on headset - other calls remain active');
        // Don't call endCall, but do update our tracking if needed
        if (this._ongoingCalls.value !== activeCalls.length) {
          this._ongoingCalls.next(activeCalls.length);
        }
      } else {
        console.log('[Jabra Service] Headset already shows no calls - skipping signalCallEnded');
      }
    } catch (error) {
      // Check if this is the expected error when there's no call to end
      if (error instanceof Error && error.message.includes('no calls in progress')) {
        console.log('[Jabra Service] Headset reports no calls in progress (expected)');
        // Still update our internal state
        this._ongoingCalls.next(0);
      } else {
        console.error('[Jabra Service] Error signaling call ended:', error);
      }
      // Ensure state is reset even on error
      this._isOnHold.next(false);
    } finally {
      // Clear processing flag after a delay to allow headset state to update
      setTimeout(() => {
        this.processingCallEndEvent = false;
      }, 1000);
    }
  }

  /**
   * Signal call rejection to the headset
   */
  async signalCallRejected(): Promise<void> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn('[Jabra Service] Cannot signal call rejection: Headset not connected or call control not available.');
      return;
    }
    
    console.log('[Jabra Service] Signaling call rejection to headset...');
    try {
      // For rejecting an incoming call, we need to explicitly use rejectIncomingCall
      this.easyCallControl.rejectIncomingCall();
      
      // Also ensure our state is updated
      this._ongoingCalls.next(0);
    } catch (error) {
      if (error instanceof Error && error.message.includes('no incoming call')) {
        console.log('[Jabra Service] No incoming call to reject (according to headset)');
      } else {
        console.error('[Jabra Service] Error signaling call rejection:', error);
      }
    }
  }

  /**
   * Signal hold/resume state to the headset
   */
  async signalHold(hold: boolean): Promise<void> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn(`[Jabra Service] Cannot signal ${hold ? 'hold' : 'resume'}: Headset not connected or call control not available.`);
      return;
    }
    
    // Avoid signaling if already in the target state
    if (this._isOnHold.value === hold) {
      console.log(`[Jabra Service] Headset hold state already matches target (${hold}). Skipping signal.`);
      return;
    }
    
    console.log(`[Jabra Service] Signaling ${hold ? 'hold' : 'resume'} to headset...`);
    try {
      if (hold) {
        await this.easyCallControl.hold();
      } else {
        await this.easyCallControl.resume();
      }
      // Update internal state tracking AFTER successful signal
      this._isOnHold.next(hold);
    } catch (error) {
      console.error(`[Jabra Service] Error signaling ${hold ? 'hold' : 'resume'}:`, error);
      // Don't revert state here, let the state observable correct it if needed
    }
  }

  /**
   * Signal mute/unmute state to the headset
   */
  async signalMute(mute: boolean): Promise<void> {
    if (!this.easyCallControl || !this._isConnected.value) {
      console.warn(`[Jabra Service] Cannot signal ${mute ? 'mute' : 'unmute'}: Headset not connected or call control not available.`);
      return;
    }
    
    // If we're already processing a mute event, don't create a loop
    if (this.processingMuteEvent) {
      console.log(`[Jabra Service] Ignoring request to signal mute=${mute} while processing another mute operation`);
      return;
    }
    
    // Avoid signaling if already in the target state
    if (this._isMuted.value === mute) {
      console.log(`[Jabra Service] Headset mute state already matches target (${mute}). Skipping signal.`);
      return;
    }
    
    console.log(`[Jabra Service] Signaling ${mute ? 'mute' : 'unmute'} to headset...`);
    try {
      // Set processing flag to prevent loops
      this.processingMuteEvent = true;
      
      if (mute) {
        await this.easyCallControl.mute();
      } else {
        await this.easyCallControl.unmute();
      }
      
      // Update internal state tracking AFTER successful signal
      this._isMuted.next(mute);
      
      // Clear processing flag after a small delay to allow state to settle
      setTimeout(() => {
        this.processingMuteEvent = false;
      }, 500);
    } catch (error) {
      console.error(`[Jabra Service] Error signaling ${mute ? 'mute' : 'unmute'}:`, error);
      // Clear processing flag even on error
      this.processingMuteEvent = false;
    }
  }

  /**
   * Clean up resources and reset the service state
   */
  dispose(): void {
    console.log('[Jabra Service] Disposing service...');
    this.ngZone.run(() => {
      this.handleDeviceRemoved(); // Clean up device-related resources
    });
    
    // Clean up all subscriptions
    this.deviceSubscriptions.forEach(sub => sub.unsubscribe());
    this.deviceSubscriptions = [];
    
    this.sipSubscriptions.forEach(sub => sub.unsubscribe());
    this.sipSubscriptions = [];
    
    if (this.autoConnectSubscription) {
      this.autoConnectSubscription.unsubscribe();
      this.autoConnectSubscription = null;
    }
    
    // Reset state
    this.jabraSdk = null;
    this.eccFactory = null;
    this.sdkInitializationPromise = null;
    this._isSdkInitialized.next(false);
    console.log('[Jabra Service] Service disposed.');
  }

  ngOnDestroy(): void {
    this.dispose();
  }
}
